import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import { Button, Text, Panel, Switch, H3, H4, Fieldset, FormGroup, Radio, Input, Dropdown, Checkbox, Small, Box } from '@bigcommerce/big-design';
import { AddIcon, ArrowDropDownIcon } from '@bigcommerce/big-design-icons';

import { updateSettings } from '../../redux/actions/settings';
import PageHeader from '../partials/PageHeader';
import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';

import UploadStoreLogo from './UploadStoreLogo';
import PaymentTerms from './PaymentTerms';
import PaymentMethods from './PaymentMethods';
import TaxSettings from './TaxSettings';

import { locationsIndex } from '../../redux/actions/locations';

import NewTaxSettingModal from './modals/NewTaxSettingModal';
import NewPaymentTermModal from './modals/NewPaymentTermModal';
import NewPaymentMethodModal from './modals/NewPaymentMethodModal';

import EditTaxSettingModal from './modals/EditTaxSettingModal';
import EditPaymentTermModal from './modals/EditPaymentTermModal';
import EditPaymentMethodModal from './modals/EditPaymentMethodModal';

import DisableTaxSettingModal from './modals/DisableTaxSettingModal';
import DisablePaymentTermModal from './modals/DisablePaymentTermModal';
import DisablePaymentMethodModal from './modals/DisablePaymentMethodModal';

const Settings = ({ payload, settings, submitSettings, locations, loadLocations }) => {
  const [submitted, setSubmitted] = useState(false);

  const [pageSize, setPageSize] = useState(settings.settings['page_size']);
  const [authorizationLine, setAuthorizationLine] = useState(settings.settings['authorization_line']);

  const [orderMultiples, setOrderMultiples] = useState(settings.settings['order_multiples']);
  const [packQuantities, setPackQuantities] = useState(settings.settings['pack_quantities']);
  const [warningsBlock, setWarningsBlock] = useState(settings.settings['warnings_block']);
  const [taxColumn, setTaxColumn] = useState( ((settings.settings['tax_column'] === undefined || settings.settings['tax_column'] === null)) ? true : settings.settings['tax_column']);

  const [pdfCorporateAddress, setPdfCorporateAddress] = useState(null);
  const [pdfCorporateEmail, setPdfCorporateEmail] = useState(settings.settings['pdf_corporate_email']);
  const [pdfCorporatePhone, setPdfCorporatePhone] = useState(settings.settings['pdf_corporate_phone']);

  const [allocateBackorders, setAllocateBackorders] = useState(settings.settings['allocate_backorder_quantities']);

  const [showNewTaxSettingModal, setShowNewTaxSettingModal] = useState(false);
  const [showNewPaymentTermModal, setShowNewPaymentTermModal] = useState(false);
  const [showNewPaymentMethodModal, setShowNewPaymentMethodModal] = useState(false);

  const [showEditTaxSettingModal, setShowEditTaxSettingModal] = useState(false);
  const [showEditPaymentTermModal, setShowEditPaymentTermModal] = useState(false);
  const [showEditPaymentMethodModal, setShowEditPaymentMethodModal] = useState(false);

  const [showDisableTaxSettingModal, setShowDisableTaxSettingModal] = useState(false);
  const [showDisablePaymentTermModal, setShowDisablePaymentTermModal] = useState(false);
  const [showDisablePaymentMethodModal, setShowDisablePaymentMethodModal] = useState(false);

  const [activeTaxSetting, setActiveTaxSetting] = useState(null);
  const [activePaymentTerm, setActivePaymentTerm] = useState(null);
  const [activePaymentMethod, setActivePaymentMethod] = useState(null);

  const [invoiceNumberOverride, setInvoiceNumberOverride] = useState(settings.settings['invoice_number_override']);
  const [purchaseOrderNumberOverride, setPurchaseOrderNumberOverride] = useState(settings.settings['purchase_order_number_override']);

  useEffect(() => {
    loadLocations(payload);
  }, []);

  useEffect(() => {
    if(!locations.isFetching && locations.status === 'success') {
      setPdfCorporateAddress(locations.corporateLocations.locations.find(location => location.id === settings.settings['pdf_corporate_address_id']));
    }
  }, [locations]);

  useEffect(() => {
    if(!settings.isFetching && settings.status === 'success') {
      setSubmitted(false);
    };
  }, [settings])

  const setSize = (event) => {
    let size = event.target.value;
    setPageSize(size);
  }

  const addressItems = () => {
    let items = [];

    locations.corporateLocations.locations.map(location => 
      items.push(
        {
         content: location.label,
         onItemClick: () => setPdfCorporateAddress(location),
         hash: location.id 
        }
      )
    );

    return items;
  }

  const validateEmail = (event) => {
    let value = event.target.value;
    setPdfCorporateEmail(value);
  }
  
  const validatePhone = (event) => {
    let value = event.target.value;
    setPdfCorporatePhone(value);
  }

  const validateInvoiceOverride = (event) => {
    let value = event.target.value;
    if(value?.length > 0) {
      setInvoiceNumberOverride(value);
    } else {
      setInvoiceNumberOverride(null);
    }
  }

  const validatePurchaseOrderOverride = (event) => {
    let value = event.target.value;
    if(value?.length > 0) {
      setPurchaseOrderNumberOverride(value);
    } else {
      setPurchaseOrderNumberOverride(null);
    }
  }

  const handleAuthLine = () => {
    setAuthorizationLine(!authorizationLine);
  }

  const handleOrderMultiples = () => {
    setOrderMultiples(!orderMultiples);
  }

  const handlePackQuantities = () => {
    setPackQuantities(!packQuantities);
  }

  const handleWarningsBlock = () => {
    setWarningsBlock(!warningsBlock);
  }

  const handleTaxColumn = () => {
    setTaxColumn(!taxColumn);
  }

  const handleAllocateBackorders = () => {
    setAllocateBackorders(!allocateBackorders);
  }

  const newSettingsJson = () => {
    return {
      page_size: pageSize,
      logo_location: settings.settings.logo_location,
      authorization_line: authorizationLine,
      pdf_corporate_address_id: pdfCorporateAddress?.id,
      pdf_corporate_email: pdfCorporateEmail,
      pdf_corporate_phone: pdfCorporatePhone,
      invoice_number_override: invoiceNumberOverride,
      purchase_order_number_override: purchaseOrderNumberOverride,
      order_multiples: orderMultiples,
      pack_quantities: packQuantities,
      warnings_block: warningsBlock,
      tax_column: taxColumn,
      allocate_backorder_quantities: allocateBackorders,
    }
  }

  const submit = () => {
    setSubmitted(true);
    submitSettings(payload, newSettingsJson());
  }

  const description = () => {
    return(
      <> 
        <Text>Upload a logo by choosing a file and clicking the "Upload Logo" button. This is the logo that is used at the top left of your PDFs for purchase orders, shipment lists and warehouse receiving orders.</Text>
        <Text>Manage your payment settings and view invoices by clicking the "View Stripe Customer Portal" link. A link will be emailed to you once you enter your email address.</Text>
        <Text>Payment terms, payment methods and tax settings can be managed in their respective panels. These options will be used in dropdown options for suppliers and purchase orders. The tax rate setting will be used as a percentage in purchase orders when no adjustment is provided.</Text>
        <Text>Select the appropriate page size from either the US Letter or A4 page size radio buttons.</Text>
        <Text>Select a corporate address to display at the top left of each page in your pucrhase orders, and set a corporate contact email and/or corporate contact phone number to appear below that address.</Text>
        <Text>When enabled, the "Include Authorization Line" option creates a line for an authorization name/signature to appear at the bottom of purchase orders.</Text>
        <Text>When you're done updating your settings, click the "Save Page Settings" button to update how purchase order PDFs will be generated.</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#943737' helpLink="bigcommerce/supplier-purchase-orders-app-settings/" />
      <SettingsPage title="App Settings" description={description()}>
        <UploadStoreLogo 
          payload={payload}
        />

        <Panel>
          <H3>Payment Settings and Invoices</H3>
          <a href="https://billing.stripe.com/p/login/8wM8z1fy45M0bfi8ww" target="_blank">View Stripe Customer Portal</a>
        </Panel>

        <PaymentTerms 
          payload={payload}
          setActivePaymentTerm={setActivePaymentTerm}
          setShowNewPaymentTermModal={setShowNewPaymentTermModal}
          setShowEditPaymentTermModal={setShowEditPaymentTermModal}
          setShowDisablePaymentTermModal={setShowDisablePaymentTermModal}
        />
        <PaymentMethods 
          payload={payload} 
          setActivePaymentMethod={setActivePaymentMethod}
          setShowNewPaymentMethodModal={setShowNewPaymentMethodModal}
          setShowEditPaymentMethodModal={setShowEditPaymentMethodModal}
          setShowDisablePaymentMethodModal={setShowDisablePaymentMethodModal}
        />
        <TaxSettings 
          payload={payload} 
          setActiveTaxSetting={setActiveTaxSetting}
          setShowNewTaxSettingModal={setShowNewTaxSettingModal}
          setShowEditTaxSettingModal={setShowEditTaxSettingModal}
          setShowDisableTaxSettingModal={setShowDisableTaxSettingModal}
        />

        <Panel>
          <H3>Product Assignment Options</H3>

          <Panel
            header="Order Multiples"
          >
            <Small>Enable this option if you have products that need to be ordered in specific multiples. For example, enable this option if an item in your store can only be ordered from a supplier in sets of 3, and you need to be warned when the quantity of that item in a Purchase Order form is not evenly divisible by 3.</Small>
            <Small>This option will be available to be set in the Supplier Product Assignment tool, and can be included in a CSV upload by adding a column to a Supplier Product Assignment CSV under a new column with the header "Order Multiple".</Small>
            <hr/>
            <H4>Enable Order Multiples</H4>
            <Switch checked={orderMultiples} onChange={handleOrderMultiples} />
          </Panel>
          <hr/>

          <Panel
            header="Pack Quantity"
          >
            <Small>Enable this option if you order products in packages that result in multiple inventory units. For example, if you order 100 packs of an item from a supplier, that will be unpacked into 100 separate items, each of which should be considered a seaparate inventory item at one of your BigCommerce locations.</Small>
            <Small>Products that don't have a pack quantity set will be treated as if they have a pack quantity of 1.</Small>
            <Small>When suggesting orders, the suggested quantity will be rounded up when a product has low inventory, so that the suggestion will at least match the threshold set in the thresholds tool.</Small>
            <Small>Products with pack quantities that are not 1 will not be fulfillable by purchase orders sent directly to customers. This is because the ordered quantity represents a different number of products than the number that would appear in a purchase order to a supplier. In the above example, a customer is ordering 1 item, not a 100 pack of items.</Small>
            <Small>This option will be available to be set in the Supplier Product Assignment tool, and can be included in a CSV upload by adding a column to a Supplier Product Assignment CSV under a new column with the header "Pack Quantity".</Small>
            <hr/>
            <H4>Enable Pack Quantity</H4>
            <Switch checked={packQuantities} onChange={handlePackQuantities} />
          </Panel>
          <hr/>


          <Button
            disabled={submitted}
            onClick={() => submit()}
          >
            Save Settings
          </Button>
        </Panel>

        <Panel>
          <H3>Purchase Order Receiving Settings</H3>

          <H4>Automatically Allocate Inventory Backorders</H4>
          <Small>When enabled, backordered quantities will be subtracted from received stock, automatically reducing both the remaining backorder quantity, and the new inventory quantity for the received item.</Small>
          <Small color="danger70">Backorders are currently a beta feature in BigCommerce. This option to automatically allocate backorders may change as the backorders feature changes in BigCommerce.</Small>
          <Switch checked={allocateBackorders} onChange={handleAllocateBackorders} />

          <hr/>

          <Button
            disabled={submitted}
            onClick={() => submit()}
          >
            Save Settings
          </Button>
        </Panel>

        <Panel>
          <H3>Purchase Order Settings</H3>

          <div className="flex-container">
            <div className="flex-1 padding-15">
              
              <H4>Warnings Block</H4>
              <Small>When enabled, warnings block will block PO creation and updates when MOQ or order multiple warnings exist.</Small>
              <Switch checked={warningsBlock} onChange={handleWarningsBlock} />
              <hr/>

              <H4>Field Name Overrides</H4>
              <Input
                label="Invoice Number"
                placeholder="Invoice Number"
                id="invoiceNumber"
                type="text"
                value={ invoiceNumberOverride }
                onChange={ (e) => validateInvoiceOverride(e) }
              />
              <Input
                label="Purchase Order Number"
                placeholder="Purchase Order Number"
                id="purchaseOrderNumber"
                type="text"
                value={ purchaseOrderNumberOverride }
                onChange={ (e) => validatePurchaseOrderOverride(e) }
              />

              <hr/>
              
              <H4>PDF Page Size</H4>
              <FormGroup>
                <Radio checked={pageSize === "LETTER"} value="LETTER" label="US Letter" onChange={(e) => setSize(e)} />
                <Radio checked={pageSize === "A4"} value="A4" label="A4" onChange={(e) => setSize(e)} />
              </FormGroup>          

              <hr/>

              <H4>Default Tax Column</H4>
              <Small>Sets the default for enabling the tax calculation on each row for POs. This appears as a column in the line items table in the PO PDF, and shows taxes calculated for each item.</Small>
              <Switch checked={taxColumn} onChange={handleTaxColumn} />

              <hr/>

              <H4>Include Authorization Line</H4>
              <Checkbox
                checked={authorizationLine}
                label={authorizationLine ? 'Enabled' : 'Disabled'}
                onChange={handleAuthLine}
              />
            </div>

            <div className="flex-1 padding-15">
              <H4>PDF Corporate Address</H4>
              { locations.status !== 'success' &&
                <LoadingSpinner size="xxSmall" statusText="Loading locations"/>
              }

              { locations.status === 'success' && locations.corporateLocations.locations.length > 0 &&
                <>
                  <Dropdown
                    items={addressItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{pdfCorporateAddress?.label || "Select Address"}</Button>}
                  />
                </>
              }
              { locations.status === 'success' && locations.corporateLocations.locations.length === 0 &&
                <Box
                  backgroundColor="warning10"
                  border="box"
                  borderRadius="normal"
                  padding="medium"
                >
                  <div className="text-center">
                    <Text>No corporate locations set. Create corporate locations to see them here.</Text>
                    <Link to="/locations">
                      <Button iconLeft={<AddIcon />}>
                        Manage locations
                      </Button>
                    </Link>
                  </div>
                </Box>
              }
              <hr/>

              <Input 
                required 
                label="Corporate Contact Email" 
                placeholder="Email" 
                id="email" 
                type="text"
                value={ pdfCorporateEmail } 
                onChange={(e) => validateEmail(e)}
              />

              <hr/>

              <Input 
                required 
                label="Corporate Contact Phone" 
                placeholder="Phone" 
                id="phone" 
                type="text"
                value={ pdfCorporatePhone } 
                onChange={(e) => validatePhone(e)}
              />
            </div>
          </div>


          <hr/>

          <Button
            disabled={submitted}
            onClick={() => submit()}
          >
            Save Settings
          </Button>
        </Panel>
        <button onClick={() => {throw new Error("Manually capture in Sentry");}}>.</button>
      </SettingsPage>

      <NewTaxSettingModal
        payload={payload}
        showNewTaxSettingModal={showNewTaxSettingModal}
        setShowNewTaxSettingModal={setShowNewTaxSettingModal}
      />

      <NewPaymentTermModal
        payload={payload}
        showNewPaymentTermModal={showNewPaymentTermModal}
        setShowNewPaymentTermModal={setShowNewPaymentTermModal}
      />

      <NewPaymentMethodModal
        payload={payload}
        showNewPaymentMethodModal={showNewPaymentMethodModal}
        setShowNewPaymentMethodModal={setShowNewPaymentMethodModal}
      />


      <EditTaxSettingModal
        payload={payload}
        showEditTaxSettingModal={showEditTaxSettingModal}
        setShowEditTaxSettingModal={setShowEditTaxSettingModal}
        taxSetting={activeTaxSetting}
      />

      <EditPaymentTermModal
        payload={payload}
        showEditPaymentTermModal={showEditPaymentTermModal}
        setShowEditPaymentTermModal={setShowEditPaymentTermModal}
        paymentTerm={activePaymentTerm}
      />

      <EditPaymentMethodModal
        payload={payload}
        showEditPaymentMethodModal={showEditPaymentMethodModal}
        setShowEditPaymentMethodModal={setShowEditPaymentMethodModal}
        paymentMethod={activePaymentMethod}
      />


      <DisableTaxSettingModal
        payload={payload}
        showDisableTaxSettingModal={showDisableTaxSettingModal}
        setShowDisableTaxSettingModal={setShowDisableTaxSettingModal}
        taxSetting={activeTaxSetting}
      />

      <DisablePaymentTermModal
        payload={payload}
        showDisablePaymentTermModal={showDisablePaymentTermModal}
        setShowDisablePaymentTermModal={setShowDisablePaymentTermModal}
        paymentTerm={activePaymentTerm}
      />

      <DisablePaymentMethodModal
        payload={payload}
        showDisablePaymentMethodModal={showDisablePaymentMethodModal}
        setShowDisablePaymentMethodModal={setShowDisablePaymentMethodModal}
        paymentMethod={activePaymentMethod}
      />
    </>
  )
}


const mapStateToProps = state => ({
  stores: state.stores,
  settings: state.settings,
  locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
  submitSettings: (payload, newSettings) => dispatch(updateSettings(payload, newSettings)),
  loadLocations: (payload) => dispatch(locationsIndex(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);